import { useLanguage } from "@/contexts/LanguageContext";
import { cn } from "@/lib/utils";
import appImage from "@/assets/application/hive_status.webp?w=240&format=webp";
import appImageSet from "@/assets/application/hive_status.webp?w=120;240;360;480&format=webp&as=srcset";
import recorderImage from "@/assets/products/hero/recorder.webp?w=320&format=webp";
import recorderImageSet from "@/assets/products/hero/recorder.webp?w=160;320;480;640&format=webp&as=srcset";
import nfcImage from "@/assets/products/hero/nfc-tag.webp?w=240&format=webp";
import nfcImageSet from "@/assets/products/hero/nfc-tag.webp?w=120;240;360;480&format=webp&as=srcset";
import "@/styles/platform-orbit.css";

export type PlatformOrbitSize = "tile" | "hero";

const ORBIT_IMAGES = [
  {
    id: "app",
    image: appImage, imageSet: appImageSet, width: 1419, height: 2796,
    imageAltKey: "field.app.hero.imageAlt",
    sizes: {
      tile: "(max-width: 469px) calc(33vw - 27px), 126px",
      hero: "(max-width: 600px) calc(33vw - 13px), 191px",
    },
  },
  {
    id: "recorder",
    image: recorderImage, imageSet: recorderImageSet, width: 689, height: 840,
    imageAltKey: "field.shop.imageAlt.recorder",
    sizes: {
      tile: "(max-width: 469px) calc(41vw - 34px), 156px",
      hero: "(max-width: 600px) calc(41vw - 16px), 238px",
    },
  },
  {
    id: "nfc",
    image: nfcImage, imageSet: nfcImageSet, width: 1254, height: 1254,
    imageAltKey: "field.shop.imageAlt.nfc-tag",
    sizes: {
      tile: "(max-width: 469px) calc(29vw - 24px), 110px",
      hero: "(max-width: 600px) calc(29vw - 12px), 168px",
    },
  },
] as const;

interface PlatformOrbitProps {
  className?: string;
  /** `tile` = homepage product card (380x250), `hero` = platform page hero (580x420). */
  size?: PlatformOrbitSize;
}

/**
 * The three platform products (app, recorder, NFC tag) turning around a
 * shared point on a tilted circle. Browsers without CSS trig functions and
 * reduced-motion users get the static composition from `platform-orbit.css`.
 */
export const PlatformOrbit = ({ className, size = "tile" }: PlatformOrbitProps) => {
  const { t } = useLanguage();

  return (
    <div className={cn("platform-orbit", `platform-orbit--${size}`, className)}>
      {ORBIT_IMAGES.map((product) => (
        <img
          key={product.id}
          className={`platform-orbit-image platform-orbit-image--${product.id}`}
          src={product.image}
          srcSet={product.imageSet}
          sizes={product.sizes[size]}
          alt={t(product.imageAltKey)}
          width={product.width}
          height={product.height}
          loading="eager"
          decoding="async"
          {...(size === "hero" && product.id === "app" ? { fetchpriority: "high" } : {})}
        />
      ))}
    </div>
  );
};
